import { useMemo } from 'react';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/nextjs';
import merge from 'deepmerge';
import { createClient } from 'graphql-ws';
import * as http from 'http';
import { isEqual } from 'lodash';
import { UserFollows } from 'types/generated/client';
import { getViewerToken } from 'services/client/token';

const USER_FOLLOWS_TYPENAME: UserFollows['__typename'] = 'UserFollows';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

export interface InitApolloOptions {
  clientState?: object;
  headers?: http.IncomingHttpHeaders;
  authToken?: string;
}

type Client = ApolloClient<NormalizedCacheObject>;
let apolloClient: Client;
const URI = process.env.GRAPHQL_URL;

/**
 * Subscriptions ride a WebSocket; everything else keeps using HTTP.
 *
 * Returns null on the server — a socket must never be opened during SSR — and the
 * link chain falls back to exactly what it was before subscriptions existed.
 *
 * `lazy` means the socket only opens when something actually subscribes, so a
 * viewer who never opens a chat never opens a connection. The close timeout lets
 * it survive a quick route change instead of tearing down and reconnecting.
 *
 * `connectionParams` is a function on purpose: graphql-ws calls it on every
 * (re)connect, so a reconnect always carries a fresh Firebase token. Hasura closes
 * the socket when a JWT expires, and the retry then re-authenticates cleanly —
 * which is the whole reason this can't be a captured string.
 */
let wsClient: ReturnType<typeof createClient> | null = null;

const createWsLink = () => {
  if (typeof window === 'undefined' || !URI) {
    return null;
  }

  // Only ever one live socket; a second call would otherwise orphan the first.
  wsClient?.dispose();

  wsClient = createClient({
    url: URI.replace(/^http/, 'ws'),
    lazy: true,
    lazyCloseTimeout: 5_000,
    keepAlive: 30_000,
    retryAttempts: 10,
    connectionParams: async () => {
      const token = await getViewerToken();

      return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    },
  });

  return new GraphQLWsLink(wsClient);
};

/**
 * Closes the socket so the next subscription reconnects and re-authenticates.
 *
 * Hasura resolves session variables once, from `connection_init`, and holds them for
 * the life of the connection — so the identity on an open socket is whoever opened
 * it, not whoever is signed in now. `resetStore()` doesn't touch that. Without this,
 * signing out and back in inside the 5s `lazyCloseTimeout` window subscribes over
 * the previous user's connection; the row filters make that empty rather than a
 * leak, but it's still the wrong identity.
 */
export const disposeWsClient = async () => {
  if (!wsClient) return;

  const client = wsClient;
  wsClient = null;
  await client.dispose();
};

const isSubscription = ({ query }: { query: Parameters<typeof getMainDefinition>[0] }) => {
  const definition = getMainDefinition(query);

  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

const withToken = setContext(async () => {
  const token = await getViewerToken();

  if (!token) {
    return {};
  }

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
});

function createApolloClient(options: InitApolloOptions = { headers: {} }) {
  const httpLink = new HttpLink({
    uri: URI,
    fetchOptions: {
      mode: 'cors',
    },
    // @ts-expect-error The headers ill always match
    headers: {
      ...(options.headers || {}),
    },
    credentials: 'same-origin', // Additional fetch() options like `credentials` or `headers`
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message }) => {
        Sentry.captureMessage(message);
        Sentry.flush(2000);
      });
    }
    if (networkError) {
      Sentry.captureException(networkError);
      Sentry.flush(2000);
    }
  });

  // Only the terminating link changes: subscriptions split off to the socket, and
  // queries/mutations traverse the same chain they always have.
  const wsLink = createWsLink();
  const terminatingLink = wsLink ? ApolloLink.split(isSubscription, wsLink, httpLink) : httpLink;

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([withToken, errorLink, terminatingLink]),
    cache: new InMemoryCache({
      typePolicies: {
        [USER_FOLLOWS_TYPENAME!]: {
          keyFields: ['followerUserId', 'followedUserId'],
        },
      },
    }),
  });
}

export function initializeApollo(initialState: any = null) {
  const _apolloClient = apolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(existingCache, initialState, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
      ],
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function addApolloState(client: Client, pageProps: any) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps: any) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeApollo(state), [state]);
  return store;
}
